import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography, SubmitLoaderButton } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button } from "components/ui";
import { EllipsisVertical, Pencil, Trash } from "lucide-react";

import checkAccess from "utils/checkAccess";
import { DeleteUserPlan } from "store/middlewares/dataManagement/UserPlan";
import { openAdminModal, closeAdminModal } from "store/slices/common/adminModal.slice";
import { useNavigate } from "react-router-dom";

/* ---------- delete confirm (same file) ---------- */
const DeleteUserPlanConfirm = ({ id }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = () => {
    setIsSubmitting(true);
    dispatch(
      DeleteUserPlan({
        id,
        onSuccess: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
        onError: () => {
          dispatch(closeAdminModal());
          setIsSubmitting(false);
        },
      }),
    );
  };

  return (
    <div className="grid gap-5 p-1">
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Trash className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          Delete this plan?
        </h2>
      </div>

      <ul className="grid gap-2">
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span className="break-words">
            This user plan will be permanently removed from the system.
          </span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>This action cannot be undone.</span>
        </li>
        <li className="flex items-start gap-2.5 text-sm leading-6 text-secondary-foreground">
          <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
          <span>Users assigned to this plan may lose their current benefits.</span>
        </li>
      </ul>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={isSubmitting}
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <SubmitLoaderButton
          type="button"
          variant="destructive"
          size="lg"
          btnText="Delete plan"
          isDisable={isSubmitting}
          onClick={handleDelete}
        />
      </div>
    </div>
  );
};
/* ----------------------------------------------- */

const UserPlanAction = ({ id }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const ActionMenuArray = [
    checkAccess("update_user_plan") && {
      label: "Edit",
      icon: <Pencil size={16} color="#475569" />,
      onClick: () => {
        navigate("/data-management/user-plan/edit", {
          state: { id },
        });
      },
    },
    checkAccess("delete_user_plan") && {
      label: "Delete",
      icon: <Trash size={16} color="#475569" />,
      onClick: () => {
        dispatch(
          openAdminModal({
            title: "",
            content: <DeleteUserPlanConfirm id={id} />,
          }),
        );
      },
    },
  ].filter(Boolean);

  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-end gap-3">
      <Button
        className="size-7 bg-[#f8f8fa]"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={handleActionMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default UserPlanAction;